import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { PDFNotificationService, pdfDefaultOptions, type IPDFViewer } from 'ngx-extended-pdf-viewer';

export interface PdfWheelScaleOptions {
  scaleFactor: number;
  drawingDelay: number;
  origin: [number, number];
}

type WheelZoomViewer = IPDFViewer & {
  updateScale(options: PdfWheelScaleOptions): void;
};

// PDF.js exposes updateScale(), but the wrapper's viewer interface omits it.
function supportsScaleUpdates(viewer: IPDFViewer): viewer is WheelZoomViewer {
  return 'updateScale' in viewer && typeof viewer.updateScale === 'function';
}

@Directive({
  selector: 'ngx-extended-pdf-viewer[appPdfWheelZoom]',
  standalone: false,
})
export class PdfWheelZoomDirective implements OnInit, OnDestroy {
  private zoomModifierPressed = false;
  private pendingScale = 0;
  private lastScale = 0;

  constructor(
    private element: ElementRef<HTMLElement>,
    private notifications: PDFNotificationService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.element.nativeElement.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
      window.addEventListener('keydown', this.onModifierChange);
      window.addEventListener('keyup', this.onModifierChange);
      window.addEventListener('blur', this.onBlur);
    });
  }

  ngOnDestroy(): void {
    this.element.nativeElement.removeEventListener('wheel', this.onWheel, true);
    window.removeEventListener('keydown', this.onModifierChange);
    window.removeEventListener('keyup', this.onModifierChange);
    window.removeEventListener('blur', this.onBlur);
  }

  private readonly onModifierChange = (event: KeyboardEvent): void => {
    this.zoomModifierPressed = event.ctrlKey || event.metaKey;
    if (!this.zoomModifierPressed) {
      this.pendingScale = this.lastScale = 0;
    }
  };

  private readonly onBlur = (): void => {
    this.zoomModifierPressed = false;
    this.pendingScale = this.lastScale = 0;
  };

  private readonly onWheel = (event: WheelEvent): void => {
    if (
      !this.zoomModifierPressed ||
      !(event.ctrlKey || event.metaKey) ||
      event.deltaY === 0 ||
      event.defaultPrevented ||
      !event.cancelable ||
      document.visibilityState === 'hidden'
    ) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element) || !target.closest('#viewerContainer')) return;

    const application = this.notifications.onPDFJSInitSignal();
    const viewer = application?.pdfViewer;
    if (!application?.pdfDocument || !viewer || application.overlayManager?.active) return;
    const page = viewer.getPageView(viewer.currentPageNumber - 1)?.div;
    if (!page || !this.element.nativeElement.contains(page)) return;
    if ('isInPresentationMode' in viewer && viewer.isInPresentationMode === true) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!supportsScaleUpdates(viewer)) {
      throw new Error('The PDF engine does not support proportional wheel zoom.');
    }

    const deltaPerStep =
      event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 3 : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? 1 : 100;
    const steps = Math.max(-1, Math.min(1, -event.deltaY / deltaPerStep));
    if (this.lastScale !== viewer.currentScale) this.pendingScale = viewer.currentScale;
    const targetScale = Math.max(viewer.minZoom, Math.min(viewer.maxZoom, this.pendingScale * 1.1 ** steps));

    viewer.updateScale({
      scaleFactor: targetScale / viewer.currentScale,
      drawingDelay: pdfDefaultOptions.defaultZoomDelay,
      origin: [event.clientX, event.clientY],
    });
    this.pendingScale = targetScale;
    this.lastScale = viewer.currentScale;
  };
}
